import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import '../styles/JournalAcces.css';

interface Entree {
  _id: string;
  action: string;
  acteur: string;
  ressource: string;
  cible?: string | null;
  adresseIp?: string | null;
  statut: number;
  echec: boolean;
  dateEvenement: string;
}

const LIBELLES: Record<string, string> = {
  connexion: 'Connexion',
  acces_refuse: 'Accès refusé',
  consultation: 'Consultation',
  modification: 'Modification',
  creation: 'Création',
  suppression: 'Suppression',
  export: 'Export',
  envoi: 'Envoi de mail',
};

const JournalAcces: React.FC = () => {
  const [entrees, setEntrees] = useState<Entree[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const [action, setAction] = useState('');
  const [acteur, setActeur] = useState('');
  const [echecsSeuls, setEchecsSeuls] = useState(false);
  const [jours, setJours] = useState(30);

  const PAR_PAGE = 50;

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const params = new URLSearchParams({
        jours: String(jours),
        page: String(page),
        parPage: String(PAR_PAGE),
      });
      if (action) params.append('action', action);
      if (acteur) params.append('acteur', acteur);
      if (echecsSeuls) params.append('echecs', 'true');

      const { data } = await api.get(`/audit?${params.toString()}`);
      setEntrees(data.entrees);
      setTotal(data.total);
    } catch {
      setErreur("Impossible de charger le journal.");
    } finally {
      setChargement(false);
    }
  }, [action, acteur, echecsSeuls, jours, page]);

  useEffect(() => { charger(); }, [charger]);

  const dateFr = (iso: string) =>
    new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

  const nbPages = Math.max(1, Math.ceil(total / PAR_PAGE));

  return (
    <div className="journal-acces">
      <h1>Journal des accès</h1>
      <p className="journal-intro">
        Trace des connexions et des actions réalisées dans l'application.
        Les entrées sont conservées 12 mois, puis supprimées automatiquement.
      </p>

      <div className="journal-filtres">
        <select value={action} onChange={(e) => { setPage(1); setAction(e.target.value); }}>
          <option value="">Toutes les actions</option>
          {Object.entries(LIBELLES).map(([cle, libelle]) => (
            <option key={cle} value={cle}>{libelle}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Filtrer par acteur (courriel)"
          value={acteur}
          onChange={(e) => { setPage(1); setActeur(e.target.value); }}
        />

        <select value={jours} onChange={(e) => { setPage(1); setJours(Number(e.target.value)); }}>
          <option value={1}>Dernières 24 h</option>
          <option value={7}>7 derniers jours</option>
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
          <option value={365}>12 derniers mois</option>
        </select>

        <label className="journal-case">
          <input
            type="checkbox"
            checked={echecsSeuls}
            onChange={(e) => { setPage(1); setEchecsSeuls(e.target.checked); }}
          />
          Échecs uniquement
        </label>

        <button type="button" onClick={charger}>Actualiser</button>
      </div>

      {erreur && <p className="journal-erreur">{erreur}</p>}
      {chargement ? (
        <p>Chargement…</p>
      ) : (
        <>
          <p className="journal-total">
            {total} entrée{total > 1 ? 's' : ''}
            {total > PAR_PAGE && ` — page ${page} sur ${nbPages}`}
          </p>

          <table className="journal-table">
            <thead>
              <tr>
                <th>Date</th><th>Action</th><th>Acteur</th>
                <th>Adresse IP</th><th>Ressource</th><th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {entrees.length === 0 && (
                <tr><td colSpan={6} className="journal-vide">Aucune entrée sur cette période.</td></tr>
              )}
              {entrees.map((e) => (
                <tr key={e._id} className={e.echec ? 'ligne-echec' : ''}>
                  <td className="col-date">{dateFr(e.dateEvenement)}</td>
                  <td><span className={`etiquette etiquette-${e.action}`}>{LIBELLES[e.action] ?? e.action}</span></td>
                  <td>{e.acteur}</td>
                  <td className="col-ip">{e.adresseIp ?? '—'}</td>
                  <td className="col-ressource">{e.ressource}</td>
                  <td className={e.echec ? 'statut-echec' : 'statut-ok'}>{e.statut}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {nbPages > 1 && (
            <div className="journal-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Précédent</button>
              <span>{page} / {nbPages}</span>
              <button type="button" disabled={page >= nbPages} onClick={() => setPage((p) => p + 1)}>Suivant</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JournalAcces;
